import { HttpErrorResponse } from '@angular/common/http';
import { CheckinConflictCode } from 'src/app/api/generated/v3/schemas/checkinConflictCode';
import type { CheckinConflictProblem } from 'src/app/api/generated/v3/schemas/checkinConflictProblem';
import { formatCpf, formatSpDateTime, orDash } from '../../admin/shared';
import type { SweetAlertOptions } from 'sweetalert2';

import {
  attachSupportCodeCopyHandler,
  buildErrorDialogOptions,
  buildSupportCodeBannerHtml,
  extractTraceId,
} from 'src/app/helpers/support-code.helpers';

/**
 * Tratamento de erro da tela de Check-ins.
 *
 * Duas coisas moram aqui: o envelope do diálogo de erro (que garante o banner
 * de código de suporte quando o backend mandou traceId) e o reconhecimento do
 * 409 de ingresso fora da janela do lote, que NÃO é um erro comum — ele abre o
 * diálogo "Ingresso Expirado" com a opção de forçar o check-in.
 *
 * IMPORTANTE: nada aqui chama `closeLoader()`. `dispararLoader`/`closeLoader`
 * são eventos de janela assíncronos: um `closeLoader()` no handler de erro
 * chega DEPOIS do Swal abrir e dispensa o diálogo que acabou de aparecer.
 * `closeLoader()` só no caminho de sucesso.
 */

export interface BaseErro {
  titulo: string;
  texto: string;
}

/**
 * Opções de um `Swal.fire` de erro. Com traceId no header `X-Trace-Id` ou no
 * corpo, o diálogo ganha o banner "CÓDIGO DE SUPORTE" com botão Copiar
 * funcional (`didOpen`); sem traceId, cai no formato simples de hoje.
 */
/**
 * 401 no meio do turno. Não existe interceptor global de 401 no app (só
 * predicados de status em http-helpers) — nem refresh de token, nem
 * re-autenticação —, então sem isto o operador via "Erro" genérico e ficava
 * numa tela que não registra mais nada, sem entender por quê.
 */
export function ehSessaoExpirada(err: unknown): boolean {
  return err instanceof HttpErrorResponse && err.status === 401;
}

export function opcoesErro(err: unknown, { titulo, texto }: BaseErro): SweetAlertOptions {
  return buildErrorDialogOptions(err, { title: titulo, text: texto });
}

interface CorpoExpirado {
  periodoCheckinLote?: { start?: unknown; end?: unknown } | null;
  type?: unknown;
  nomeParticipante?: unknown;
  nomeLote?: unknown;
}

function corpo(err: unknown): CorpoExpirado {
  const http = err as { error?: unknown } | null | undefined;
  const dados = (http?.error ?? err) as CorpoExpirado | null | undefined;
  return dados && typeof dados === 'object' ? dados : {};
}

/**
 * O 409 específico de ingresso fora da janela do lote.
 *
 * Condição idêntica à da tela antiga (`fazerCheckinPorCodigo`): status 409 E
 * (corpo com `periodoCheckinLote` OU `type === 'IngressoExpiradoException'`).
 * O `type` sozinho não basta e o `periodoCheckinLote` sozinho também não —
 * qualquer outro 409 (conflito de escrita, por exemplo) segue no diálogo de
 * erro normal, senão a tela ofereceria "Forçar Check-in" para um erro que
 * forçar não resolve.
 */
/**
 * `code` do corpo do erro. É o discriminador documentado no backend.
 */
function codigoErro(err: unknown): string {
  const corpo = (err as { error?: unknown } | null)?.error ?? err;
  const code = (corpo as { code?: unknown } | null)?.code;
  if (typeof code === 'string') return code;
  // O expirado serializa o CodesEnum, que pode vir como objeto.
  const aninhado = (code as { code?: unknown } | null)?.code;
  return typeof aninhado === 'string' ? aninhado : '';
}

/**
 * Aceita o `HttpErrorResponse` real E um objeto solto com `status` — a tela
 * antiga discriminava por `error?.status`, e apertar isso para só o tipo do
 * Angular quebraria qualquer caminho que re-embrulhe o erro.
 */
function ehConflito(err: unknown): boolean {
  return (err as { status?: unknown } | null)?.status === 409;
}

/**
 * 409 CHECKIN_JA_REALIZADO — o ingresso já entrou. NÃO é o mesmo que fora do
 * período: aqui não há o que forçar, e o corpo traz nome, lote, horário e
 * operador do check-in original, que é a informação que o operador na porta
 * precisa para resolver a fila.
 */
export function ehCheckinJaRealizado(err: unknown): boolean {
  return ehConflito(err) && codigoErro(err) === CheckinConflictCode.CHECKIN_JA_REALIZADO;
}

export interface DadosCheckinJaRealizado {
  nome: string;
  nomeLote: string;
  operador: string;
  email: string;
  cpf: string;
  dataCheckin: string;
}

/**
 * Duas origens, um formato, degradando CAMPO A CAMPO: o que faltou vira `--`,
 * o resto aparece.
 *
 * Desde o contrato 3.8.0 a v3 manda `checkinOriginal` (`CheckinOriginalDto`):
 * contexto operacional tipado e SEM PII, de propósito — não vem e-mail nem CPF.
 * O 409 do monólito manda os mesmos dados achatados na raiz, com os dois. O
 * tipado tem precedência.
 *
 * Existiu aqui um `dadosCheckinJaRealizado` que devolvia `null` quando faltava
 * QUALQUER um de nome/operador/horário, e nesse caso o diálogo caía num texto
 * simples. Foi apagado: na porta, um corpo incompleto derrubava o cartão
 * inteiro — some justamente quem é o portador, quem registrou e quando, que é
 * a informação que encerra a discussão. Faltar um campo não é motivo para
 * esconder os outros.
 */
export function dadosJaRealizadoParcial(err: unknown): DadosCheckinJaRealizado {
  const c = ((err as { error?: unknown } | null)?.error ?? err) as Record<string, unknown> | null;
  const original = c?.['checkinOriginal'] as Record<string, unknown> | null | undefined;
  const fonte = original ?? c ?? {};

  return {
    nome: orDash(original ? fonte['participante'] : fonte['nome']),
    nomeLote: orDash(original ? fonte['loteNome'] : fonte['nomeLote']),
    operador: orDash(original ? fonte['operador'] : fonte['nomeOperador']),
    // `CheckinOriginalDto` não carrega PII: com a forma tipada os dois somem, e
    // `blocoPessoa` esconde o que vem `--` em vez de renderizar rótulo vazio.
    email: original ? '--' : orDash(c?.['email']),
    cpf: !original && c?.['cpf'] ? formatCpf(c['cpf']) : '--',
    dataCheckin: formatSpDateTime(
      original ? fonte['realizadoEm'] : fonte['dataCheckin'],
    ).replace(/:\d{2}$/, ''),
  };
}

/**
 * 403 do fluxo v3: a permissão do usuário neste evento não inclui registrar
 * check-in. Não é "erro" nem "sessão expirada" — entrar de novo não resolve,
 * quem resolve é quem administra o evento.
 */
export function ehSemPermissao(err: unknown): boolean {
  return (err as { status?: unknown } | null)?.status === 403;
}

/**
 * Indisponibilidade: rede caiu, gateway ou serviço fora. A escrita já repetiu
 * com a MESMA `Idempotency-Key` antes de chegar aqui, então a operação pode ter
 * sido gravada — a tela manda conferir a lista, não tentar de novo às cegas.
 */
export function ehIndisponivel(err: unknown): boolean {
  const status = (err as { status?: unknown } | null)?.status;
  return status === 0 || status === 408 || status === 502 || status === 503 || status === 504;
}

/**
 * Texto do erro v3, que é RFC 7807: `detail` é a frase escrita para humano e
 * `title` o rótulo do problema. O `message` do envelope legado entra na mesma
 * fila. Sem nenhum dos três, vale o padrão da tela — nunca um JSON cru na cara
 * do operador.
 *
 * Serve só para EXIBIR. Nenhuma decisão da tela olha este texto: quem decide é
 * `code`/`forceAllowed`, e é por isso que `ehIngressoExpirado` não passa por
 * aqui.
 */
export function detalheProblema(err: unknown, padrao: string): string {
  const corpo = ((err as { error?: unknown } | null)?.error ?? err) as
    | { detail?: unknown; message?: unknown; title?: unknown }
    | null
    | undefined;
  for (const campo of [corpo?.detail, corpo?.message, corpo?.title]) {
    if (typeof campo === 'string' && campo.trim()) return campo;
  }
  return padrao;
}

/**
 * 409 tipado de período inválido — o único que oferece Forçar Check-in.
 * `detail` e formatos legados nunca participam da decisão.
 */
export function ehIngressoExpirado(err: unknown): boolean {
  const problema = conflitoCheckinTipado(err);
  return (
    problema?.code === CheckinConflictCode.CHECKIN_FORA_DA_JANELA &&
    problema.forceAllowed === true
  );
}

function conflitoCheckinTipado(err: unknown): CheckinConflictProblem | null {
  if (!ehConflito(err)) return null;
  const dados = (err as { error?: unknown } | null)?.error ?? err;
  if (!dados || typeof dados !== 'object') return null;
  const problema = dados as Partial<CheckinConflictProblem>;
  if (typeof problema.forceAllowed !== 'boolean') return null;
  if (
    problema.code !== CheckinConflictCode.CHECKIN_JA_REALIZADO &&
    problema.code !== CheckinConflictCode.CHECKIN_FORA_DA_JANELA
  ) {
    return null;
  }
  return problema as CheckinConflictProblem;
}


export interface DadosIngressoExpirado {
  nomeParticipante: string;
  nomeLote: string;
  /** `<início> até <fim>`, ou `'Não informado'` quando o backend não mandou. */
  periodo: string;
}

/**
 * Dados já formatados para o diálogo "Ingresso Expirado" / Forçar Check-in.
 *
 * As datas saem fixadas em America/Sao_Paulo (`formatSpDateTime`), e não no
 * fuso da máquina como o `toLocaleString('pt-BR')` da tela antiga: o backend
 * responde em UTC e o resto do módulo já exibe tudo no fuso do evento — o
 * período do lote precisa bater com a janela mostrada na mesma linha.
 */
export function dadosIngressoExpirado(err: unknown): DadosIngressoExpirado {
  const dados = corpo(err);
  const periodo = dados.periodoCheckinLote;

  return {
    nomeParticipante: texto(dados.nomeParticipante),
    nomeLote: texto(dados.nomeLote),
    periodo: periodo ? `${data(periodo.start)} até ${data(periodo.end)}` : 'Não informado',
  };
}

/** Paridade com a tela antiga: ausente vira `'N/A'`, não string vazia. */
function texto(v: unknown): string {
  return v === null || v === undefined || v === '' ? 'N/A' : String(v);
}

function data(v: unknown): string {
  const formatada = formatSpDateTime(v);
  return formatada === '--' ? 'N/A' : formatada;
}

/**
 * Acrescenta o banner de código de suporte a um diálogo montado à mão.
 *
 * Os dois 409 (já realizado e fora do período) não passam por `opcoesErro`:
 * têm corpo e botões próprios. Sem isto perdiam o traceId — e é justamente
 * neles que o operador precisa escalar ("o sistema diz que já entrou, a pessoa
 * jura que não"). Pego rodando o smoke, não no teste.
 */
export function comCodigoDeSuporte(
  err: unknown,
  opcoes: SweetAlertOptions,
): SweetAlertOptions {
  const traceId = extractTraceId(err);
  if (!traceId) return opcoes;
  return {
    ...opcoes,
    html: `${opcoes.html ?? ''}${buildSupportCodeBannerHtml(traceId)}`,
    didOpen: (popup) => attachSupportCodeCopyHandler(popup, traceId),
  };
}
